import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import type { Bet, Match } from '../api/types';
import { flagOf, matchHasBrasil } from '../lib/teams';
import ScoreInput from '../components/ScoreInput';

interface GuessDraft {
  match_id: number;
  guess_home: number;
  guess_away: number;
}

export default function BetPage() {
  const { betId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: bet } = useQuery({
    queryKey: ['bet', betId],
    queryFn: async () => (await api.get<Bet>(`/bets/${betId}`)).data,
    enabled: !!betId,
    refetchInterval: (q) => {
      const b = q.state.data as Bet | undefined;
      return b && (b.status === 'live' as any || b.guesses.some((g) => g.points_awarded > 0))
        ? 30_000
        : false;
    },
  });

  const { data: matches } = useQuery({
    queryKey: ['matches', bet?.round_id],
    queryFn: async () => (await api.get<Match[]>(`/rounds/${bet!.round_id}/matches`)).data,
    enabled: !!bet?.round_id,
  });

  const [drafts, setDrafts] = useState<Record<number, GuessDraft>>({});

  useEffect(() => {
    if (!bet || !matches) return;
    const initial: Record<number, GuessDraft> = {};
    // Default 0 x 0 pra todo jogo da rodada
    matches.forEach((m) => {
      initial[m.id] = { match_id: m.id, guess_home: 0, guess_away: 0 };
    });
    // Sobrepõe com palpites já salvos
    bet.guesses.forEach((g) => {
      initial[g.match_id] = {
        match_id: g.match_id,
        guess_home: g.guess_home,
        guess_away: g.guess_away,
      };
    });
    setDrafts(initial);
  }, [bet, matches]);

  const locked = bet?.status === 'paid';
  const isFree = bet?.amount_cents === 0;

  const allFilled = useMemo(() => {
    if (!matches) return false;
    return matches.every(
      (m) =>
        drafts[m.id] !== undefined &&
        drafts[m.id].guess_home >= 0 &&
        drafts[m.id].guess_away >= 0,
    );
  }, [matches, drafts]);

  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const saveGuesses = useMutation({
    mutationFn: async () => {
      const payload = Object.values(drafts);
      const { data } = await api.put<Bet>(`/bets/${betId}/guesses`, payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bet', betId] });
      setFeedback({ kind: 'ok', text: '✓ Rascunho salvo' });
      setTimeout(() => setFeedback(null), 2500);
    },
    onError: (err: any) => {
      setFeedback({
        kind: 'err',
        text: err.response?.data?.message || 'Erro ao salvar rascunho',
      });
    },
  });

  function setGuess(matchId: number, side: 'home' | 'away', value: number) {
    setDrafts((prev) => ({
      ...prev,
      [matchId]: {
        match_id: matchId,
        guess_home: side === 'home' ? value : prev[matchId]?.guess_home ?? 0,
        guess_away: side === 'away' ? value : prev[matchId]?.guess_away ?? 0,
      },
    }));
  }

  async function handleProceedToPayment() {
    await saveGuesses.mutateAsync();
    navigate(`/bet/${betId}/pagamento`);
  }

  if (!bet || !matches) return <div>Carregando…</div>;

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Bolão — Rodada {bet.round_id}</h2>
          {isFree && !locked && (
            <div className="text-xs text-brand-600">Aposta grátis • sem pagamento</div>
          )}
          {locked && (
            <div className="text-xs text-brand-600">
              Confirmado em {new Date(bet.paid_at!).toLocaleString('pt-BR')} — palpites travados
            </div>
          )}
        </div>
        {locked && <div className="text-right text-sm">Pontos: <strong>{bet.total_points}</strong></div>}
      </header>

      <div className="space-y-2">
        {matches.map((m) => {
          const guess = drafts[m.id];
          const points = bet.guesses.find((g) => g.match_id === m.id)?.points_awarded ?? 0;
          const isLive = m.status === 'live';
          const isFinished = m.status === 'finished';
          const brHere = matchHasBrasil(m.team_home, m.team_away);
          const cardClass = isLive
            ? 'bg-red-50 border-red-200'
            : brHere
            ? 'bg-yellow-50 border-l-4 border-l-brand-500'
            : 'bg-white border-slate-200';
          return (
            <div
              key={m.id}
              className={`rounded-lg border p-3 ${cardClass}`}
            >
              <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
                <span>
                  {new Date(m.match_date).toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  {m.group_name && <span className="ml-2">• Grupo {m.group_name}</span>}
                </span>
                {isLive && <span className="text-red-600 font-bold animate-pulse">🔴 AO VIVO</span>}
                {isFinished && <span className="text-slate-600">Finalizado</span>}
              </div>

              <div className={`flex items-center justify-between gap-2 ${brHere ? 'font-bold' : 'font-medium'}`}>
                <div className="flex-1 min-w-0 text-right truncate">
                  <span className="mr-1">{flagOf(m.team_home)}</span>
                  <span className="text-sm sm:text-base">{m.team_home}</span>
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  <ScoreInput
                    value={guess?.guess_home ?? ''}
                    onChange={(v) => setGuess(m.id, 'home', v)}
                    disabled={locked}
                    ariaLabel={`Palpite ${m.team_home}`}
                  />
                  <span className="text-slate-400">×</span>
                  <ScoreInput
                    value={guess?.guess_away ?? ''}
                    onChange={(v) => setGuess(m.id, 'away', v)}
                    disabled={locked}
                    ariaLabel={`Palpite ${m.team_away}`}
                  />
                </div>

                <div className="flex-1 min-w-0 truncate">
                  <span className="mr-1">{flagOf(m.team_away)}</span>
                  <span className="text-sm sm:text-base">{m.team_away}</span>
                </div>
              </div>

              {(isFinished || isLive || m.score_home !== null) && (
                <div className="mt-2 flex items-center justify-between text-xs">
                  <span className="text-slate-600">
                    Resultado:{' '}
                    <strong>
                      {m.score_home ?? '—'} × {m.score_away ?? '—'}
                    </strong>
                  </span>
                  {isFinished && (
                    <span className="text-brand-600 font-bold">
                      {points} {points === 1 ? 'pt' : 'pts'}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!locked && (
        <div className="sticky bottom-0 bg-slate-50 py-3 -mx-4 px-4 border-t space-y-2">
          {feedback && (
            <div
              className={`text-sm text-center rounded px-3 py-2 ${
                feedback.kind === 'ok'
                  ? 'bg-brand-500/10 text-brand-600'
                  : 'bg-red-100 text-red-700'
              }`}
            >
              {feedback.text}
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
            <button
              className="btn btn-secondary w-full sm:w-auto"
              onClick={() => saveGuesses.mutate()}
              disabled={saveGuesses.isPending}
            >
              {saveGuesses.isPending ? 'Salvando…' : 'Salvar rascunho'}
            </button>
            <button
              className="btn btn-primary w-full sm:w-auto"
              disabled={!allFilled || saveGuesses.isPending}
              onClick={handleProceedToPayment}
            >
              {isFree ? 'Salvar e confirmar' : 'Salvar e ir pro pagamento'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
